/**
 * Shared persistence for the RodeoEventDialog (the fixed/dynamic prize editor,
 * adjustments, prize rounds and percentage splits).
 *
 * This was lifted out of EventSection (My Rodeos → event → pencil) so the Prize
 * Management tab can mount the SAME editor against the SAME save path. The card
 * asked for the prize editor to be reused rather than forked, and a second copy
 * of this save — with its server-side round diff-sync, its null-vs-0 coercions
 * and its performance-junction diff — is exactly how the two surfaces would start
 * writing subtly different rows.
 *
 * EventSection and DelegatePrizeManagementPage both call `useSaveRodeoEventConfig`.
 * There is one implementation; change it here and both surfaces move together.
 */

import { useQueryClient } from '@tanstack/react-query';
import type { RodeoEventDialogValues } from '@/components/rodeo-wizard/RodeoEventDialog';
import {
  useUpdateEvents,
  eventsKeys,
} from '@/api/generated_hooks/generated-events-hooks';
import { rodeoRoundConfigsKeys } from '@/api/generated_hooks/generated-rodeo_round_configs-hooks';
import { httpClient } from '@/api/http-client';
import {
  usePerformanceEventsClient,
  performanceEventsKeys,
} from '@/api/generated_hooks/generated-performance_events-hooks';
import { performancesKeys } from '@/api/generated_hooks/generated-performances-hooks';

/** A `rodeo_round_configs` row as the read hooks return it. */
export interface ExistingRound {
  id: string;
  round_number: number | null;
  round_name: string | null;
  is_go_round: boolean | null;
  is_short_round: boolean | null;
  advancing_count: number | null;
  performance_id: string | null;
  metadata: unknown;
  /**
   * Finals run-order source (`draw` | `ytd_*` | `current_*`). Set by a
   * delegate from the live-scoring board, not by this dialog — so the save
   * carries the stored value forward per round_number rather than resetting it.
   */
  run_order_mode?: string | null;
}

/** A `performance_events` junction row. */
export interface ExistingJoin {
  id: string;
  performance_id: string;
  max_contestants?: number | null;
}

/**
 * Sort rounds the way the dialog needs them: Average pinned last, everything
 * else by round number. The dialog re-renumbers rounds 1..N on submit, so the
 * order it is hydrated with is the order it writes back.
 */
function sortRoundsAvgLast(rounds: ExistingRound[]): ExistingRound[] {
  return [...rounds].sort((a, b) => {
    const aAvg = !!(a.metadata as any)?.is_average;
    const bAvg = !!(b.metadata as any)?.is_average;
    if (aAvg !== bAvg) return aAvg ? 1 : -1;
    return (a.round_number ?? 0) - (b.round_number ?? 0);
  });
}

/** Hydrate the dialog from an events row + its rounds + its perf junctions. */
export function buildEventDialogInitialValues(
  event: any,
  existingRounds: ExistingRound[] | undefined,
  existingJoins: ExistingJoin[] | undefined,
): RodeoEventDialogValues {
  const meta = (event?.metadata as any) ?? {};
  const prizes = meta.prizes ?? {};

  const eventTypeLabel: string = event?.trig_event_type_name || event?.event_name || '';
  const customEventName: string =
    typeof event?.event_name === 'string' &&
    event.event_name.trim().length > 0 &&
    event.event_name !== event?.trig_event_type_name
      ? event.event_name
      : '';

  return {
    event_name: customEventName,
    event_type: eventTypeLabel,
    event_category: (meta.event_category as 'timed' | 'scored') ?? 'timed',
    entry_fee: event?.entry_fee ?? null,
    transaction_fee: event?.transaction_fee ?? null,
    contestant_limit: event?.max_contestants ?? null,
    contestants_per_performance: event?.contestants_per_performance ?? null,
    non_member_surcharge: meta?.non_member_surcharge ?? null,
    allow_multiple_registrations: !!event?.allow_multiple_registrations,
    max_registrations_per_user: event?.max_registrations_per_user ?? null,
    enforce_balanced_positions: !!event?.enforce_balanced_positions,
    performance_assignments: (existingJoins ?? []).map((j) => ({
      performance_key: j.performance_id,
      max_contestants: j.max_contestants ?? null,
    })),
    rounds: sortRoundsAvgLast(existingRounds ?? []).map((r) => {
      const rMeta = (r.metadata as any) ?? {};
      const rPrizes = rMeta.prizes ?? {};
      return {
        round_number: r.round_number ?? 1,
        round_name: r.round_name ?? '',
        is_go_round: !!r.is_go_round,
        is_short_round: !!r.is_short_round,
        advancing_count: r.advancing_count ?? null,
        is_average: !!rMeta.is_average,
        // Explicit per-round performance. The Avg row is a payout aggregate,
        // not a run, so it never carries one.
        performance_key: rMeta.is_average ? null : (r.performance_id ?? null),
        has_prizes: !!rPrizes.has_prizes,
        round_prize_total: Number(rPrizes.round_prize_total) || 0,
        is_progressive: !!rPrizes.is_progressive,
        places: Array.isArray(rPrizes.places) ? rPrizes.places : [],
      };
    }),
    total_prize_amount: event?.total_prize_amount ?? prizes.total_prize_amount ?? null,
    prize_adjustments: Array.isArray(prizes.adjustments) ? prizes.adjustments : [],
    prize_allocation_mode:
      event?.prize_allocation_mode === 'dynamic' ? 'dynamic' : 'fixed',
    entry_fee_prize_portion: event?.entry_fee_prize_portion ?? null,
    entry_fee_prize_portion_is_percentage:
      !!event?.entry_fee_prize_portion_is_percentage,
    entry_fee_prize_portion_is_fee_deduction:
      !!event?.entry_fee_prize_portion_is_fee_deduction,
    display_prize_to_contestants: !!event?.display_prize_to_contestants,
    show_prize_amounts_in_results: event?.show_prize_amounts_in_results !== false,
    show_prize_amounts_before_complete: !!event?.show_prize_amounts_before_complete,
    default_adjustment_overrides:
      (event?.default_adjustment_overrides as Record<string, any>) ?? {},
  };
}

export interface SaveRodeoEventConfigArgs {
  event: any;
  rodeoId: string;
  values: RodeoEventDialogValues;
  existingRounds: ExistingRound[] | undefined;
  existingJoins: ExistingJoin[] | undefined;
  /**
   * Last-resort performance for rounds left on "Default" when the event has no
   * performance assignments at all. The event's first assigned performance
   * wins over this.
   */
  fallbackPerformanceId: string | null;
}

/**
 * Persist the dialog's output: the `events` row, then the new
 * `performance_events` joins, then ONE server call that diff-syncs
 * `rodeo_round_configs`, then the dropped `performance_events` joins.
 * Throws on failure — callers surface the message in their own dialog.
 */
export function useSaveRodeoEventConfig() {
  const queryClient = useQueryClient();
  const updateEventMutation = useUpdateEvents();
  const performanceEventsClient = usePerformanceEventsClient();

  const saveEventConfig = async ({
    event,
    rodeoId,
    values,
    existingRounds,
    existingJoins,
    fallbackPerformanceId,
  }: SaveRodeoEventConfigArgs) => {
    // Custom name is optional — store NULL when blank so the title resolver
    // falls back to the discipline (`trig_event_type_name`).
    const trimmedName = values.event_name.trim();
    await updateEventMutation.mutateAsync({
      id: event.id,
      data: {
        event_name: trimmedName.length > 0 ? trimmedName : null,
        trig_event_type_name: values.event_type,
        entry_fee: values.entry_fee ?? 0,
        // Preserve null on "Reset to auto" so the backend payment service falls
        // back to the Stripe-derived fee. Coercing to 0 here would render the
        // event as "Free" and read back as a manual $0 override.
        transaction_fee: values.transaction_fee,
        total_prize_amount: values.total_prize_amount ?? 0,
        prize_allocation_mode: values.prize_allocation_mode ?? 'fixed',
        entry_fee_prize_portion: values.entry_fee_prize_portion ?? 0,
        entry_fee_prize_portion_is_percentage:
          !!values.entry_fee_prize_portion_is_percentage,
        entry_fee_prize_portion_is_fee_deduction:
          !!values.entry_fee_prize_portion_is_fee_deduction,
        display_prize_to_contestants: !!values.display_prize_to_contestants,
        show_prize_amounts_in_results: values.show_prize_amounts_in_results !== false,
        show_prize_amounts_before_complete:
          !!values.show_prize_amounts_before_complete,
        default_adjustment_overrides: values.default_adjustment_overrides ?? {},
        // Blank/0 means "Unlimited" → null. A stale 0 round-tripped into the
        // events row would trip the capacity check as "always full".
        max_contestants:
          typeof values.contestant_limit === 'number' && values.contestant_limit > 0
            ? values.contestant_limit
            : null,
        // Same null-on-non-positive coercion — the DB CHECK rejects 0/negative.
        contestants_per_performance:
          typeof values.contestants_per_performance === 'number' &&
          values.contestants_per_performance > 0
            ? values.contestants_per_performance
            : null,
        allow_multiple_registrations: !!values.allow_multiple_registrations,
        // CHECK requires 1..10 and the backend's TypeBox validator coerces null
        // on numeric fields to 0, which would trip the lower bound.
        max_registrations_per_user: values.allow_multiple_registrations
          ? Math.min(10, Math.max(1, values.max_registrations_per_user ?? 1))
          : 1,
        // Balanced Header/Heeler split. Meaningless without multi-entry, so
        // it clears alongside the cap.
        enforce_balanced_positions:
          !!values.allow_multiple_registrations &&
          !!values.enforce_balanced_positions,
        metadata: {
          ...((event?.metadata as Record<string, unknown>) ?? {}),
          event_category: values.event_category,
          // Persist null when blank/0 so the dialog reads back as "Use rodeo
          // default" instead of an explicit $0 override.
          non_member_surcharge:
            typeof values.non_member_surcharge === 'number' &&
            values.non_member_surcharge > 0
              ? values.non_member_surcharge
              : null,
          prizes: {
            total_prize_amount: values.total_prize_amount,
            adjustments: values.prize_adjustments,
          },
        },
      } as any,
    });

    // performance_events junction, first half: create the new joins. The
    // server validates each round's performance against this junction, so a
    // round the user just pointed at a newly-picked performance needs the
    // join to exist BEFORE the rounds land. Dropped joins are removed last.
    const priorJoinRows = (existingJoins ?? []) as ExistingJoin[];
    const priorPerfIdSet = new Set(priorJoinRows.map((j) => j.performance_id));
    const nextPerfIdSet = new Set(
      values.performance_assignments.map((a) => a.performance_key),
    );
    const toCreate = values.performance_assignments.filter(
      (a) => !priorPerfIdSet.has(a.performance_key),
    );
    const toDelete = priorJoinRows.filter((j) => !nextPerfIdSet.has(j.performance_id));

    await Promise.all(
      toCreate.map((a) =>
        performanceEventsClient.create({
          event_id: event.id,
          performance_id: a.performance_key,
          max_contestants: a.max_contestants ?? null,
          // Required by CreatePerformanceEventsSchema. 'pending' matches the
          // slack-evaluation service's default; it flips slack rows to
          // run/no_run once entry counts settle. Non-slack joins ignore it.
          slack_run_status: 'pending',
        } as any),
      ),
    );

    // Rounds: ONE server call. The workflow endpoint diff-syncs
    // rodeo_round_configs by round_number — matched rows are updated in place
    // (same id), new numbers inserted, dropped numbers deleted last — so a
    // rejected write can never leave the event with zero rounds. (The
    // previous per-row delete-then-create did exactly that on a live finals
    // rodeo when the generated create schema started rejecting the payload.)
    //
    // Each round writes the performance the dialog explicitly holds for it.
    // A round left on "Default" (null) falls back to the event's first
    // assigned performance — sourced from the assignments the user just
    // saved rather than whatever list the caller happened to hold. The Avg
    // row is a payout aggregate, not a run: NULL.
    const assignedPerfIds = new Set(
      values.performance_assignments.map((a) => a.performance_key),
    );
    const defaultPerfId =
      values.performance_assignments[0]?.performance_key ?? fallbackPerformanceId;
    // run_order_mode is a delegate's live-scoring choice, not something this
    // dialog edits. Carry the stored value forward per round_number so a
    // prize edit can't reset it; a round with no prior row gets the server's
    // default ('draw').
    const priorRunOrderMode = new Map(
      (existingRounds ?? [])
        .filter((r) => r.round_number != null && r.run_order_mode)
        .map((r) => [r.round_number as number, r.run_order_mode as string]),
    );
    await httpClient.post(`/jackpot-ops/rodeo/${rodeoId}/round-configs`, {
      event_id: event.id,
      round_configs: values.rounds.map((r, i) => {
        // Guard against a stale key (a perf unassigned outside the dialog):
        // never write a performance_id the event doesn't actually run in.
        const perfId = r.is_average
          ? null
          : r.performance_key && assignedPerfIds.has(r.performance_key)
            ? r.performance_key
            : defaultPerfId;
        const roundNumber = i + 1;
        return {
          round_number: roundNumber,
          round_name: r.round_name || null,
          performance_id: perfId ?? undefined,
          is_go_round: !!r.is_go_round,
          is_short_round: !!r.is_short_round,
          advancing_count:
            r.advancing_count && r.advancing_count > 0 ? r.advancing_count : undefined,
          run_order_mode: priorRunOrderMode.get(roundNumber),
          metadata: {
            is_average: !!r.is_average,
            prizes: {
              has_prizes: !!r.has_prizes,
              round_prize_total: Number(r.round_prize_total) || 0,
              is_progressive: !!r.is_progressive,
              places: r.places,
            },
          },
        };
      }),
    });

    // performance_events junction, second half: drop the joins the user
    // removed. Runs after the rounds so a round-sync failure leaves the
    // event's performances exactly as they were.
    await Promise.all(toDelete.map((j) => performanceEventsClient.delete(j.id)));

    queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.all });
    queryClient.invalidateQueries({ queryKey: eventsKeys.all });
    queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
    queryClient.invalidateQueries({ queryKey: performancesKeys.all });
  };

  return { saveEventConfig };
}
